import { fetchAllPages } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import { ErrorHandler } from '@frontend/error-handler';
import { changeData, useAccountRepository } from '@frontend/repository';
import { RoleExtendedClient } from '@frontend/role/api';
import { RoleExtended } from '@frontend/role/types';
import { useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { RolesAndLicensesProps } from './roles-and-licenses.component';

export const convertRoles = (roleArray: RoleExtended[]) => {
    const licenseSet = new Set<string>();
    const rolesToAdd = new Map<string, RoleExtended>();

    roleArray.forEach((role) => {
        if (role.item === EntityType.FUNCTIONALITY) {
            licenseSet.add(role.uid);
            return;
        }

        const roleKey = role.role_id ?? role.id;
        const existingRole = rolesToAdd.get(roleKey);
        if (existingRole && !existingRole.group) return;
        if (existingRole?.group && role.group) return;
        rolesToAdd.set(roleKey, role);
    });

    return { licenseSet, rolesToAdd };
};

export const useRolesAndLicenses = (props: RolesAndLicensesProps) => {
    const { currentAccount } = useAccountRepository();
    const dispatch = useAppDispatch();
    const entityType = props.entity_type ?? EntityType.USER;
    const ROLE_LIST_ID = `role-list-${entityType}-${props.entity_id}`;
    const listState = useAppSelector((state) => state.lists);
    const list = listState[ROLE_LIST_ID];
    const listReady = !!list;
    const listIsLoading = !!list?.isLoading;
    const [functionalities, setFunctionalities] = useState<Set<string>>(new Set());
    const [roles, setRoles] = useState<Map<string, RoleExtended>>(new Map());
    const roleItems = useMemo(() => Array.from(roles.values()), [roles]);

    useEffect(() => {
        let cancelled = false;

        setRoles(new Map());
        setFunctionalities(new Set());

        fetchAllPages((params) => RoleExtendedClient.fetchEntityRolesExtended(currentAccount, entityType, props.entity_id, params))
            .then((rolesResponse) => {
                if (cancelled) return;
                const { licenseSet, rolesToAdd } = convertRoles(rolesResponse);
                setRoles(rolesToAdd);
                setFunctionalities(licenseSet);
            })
            .catch((error) => {
                if (!cancelled) ErrorHandler.handleError({ error });
            });

        return () => {
            cancelled = true;
        };
    }, [currentAccount, entityType, props.entity_id]);

    useEffect(() => {
        if (!listReady || listIsLoading) return;
        dispatch(changeData({ listId: ROLE_LIST_ID, data: roleItems, replace: true }));
    }, [dispatch, listIsLoading, listReady, ROLE_LIST_ID, roleItems]);

    return {
        entityType,
        functionalities,
        roles,
        roleItems,
        ROLE_LIST_ID,
        dispatch
    };
};
